import React from 'react';
import { Logo } from './Logo';
import { buildCleanPath } from '../utils/navigation';
import { MapPin, Phone, Mail, Clock, ShieldCheck, ArrowUpRight, MessageCircle } from 'lucide-react';

interface FooterProps {
  onNavigate: (page: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();

  const handleNav = (page: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    onNavigate(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-950 text-slate-300 border-t border-slate-800">
      {/* Top Banner / Callout */}
      <div className="border-b border-slate-800/80 bg-gradient-to-r from-sky-950/40 via-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-sky-400 font-semibold mb-1">
                Engineering Everyday Convenience Across East Africa
              </p>
              <h3 className="text-lg sm:text-xl md:text-lg lg:text-xl xl:text-[1.375rem] font-bold text-white tracking-tight lg:whitespace-nowrap">
                Ready to launch or upgrade your vending & purification plant?
              </h3>
              <p className="text-slate-400 text-sm mt-1 max-w-2xl">
                Consult with our engineering technicians in Nairobi. We fabricate, install, test, and support your machines.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <a
                href="https://wa.me/254727072393?text=Hello%20Saints%20Tech%20Solutions,%20I%20am%20inquiring%20about%20your%20vending%20machines%20and%20purification%20systems."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg shadow-sm transition-colors cursor-pointer"
              >
                <MessageCircle className="w-4 h-4" />
                Chat on WhatsApp
              </a>
              <a
                href="/contact"
                onClick={(e) => handleNav('contact', e)}
                className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-slate-100 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors cursor-pointer"
              >
                <span>Visit Our Office</span>
                <ArrowUpRight className="w-4 h-4 text-sky-400" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Column 1: Brand & Identity */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white/5 p-3 rounded-xl inline-block border border-white/10">
              <Logo size="md" variant="horizontal" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              Saints Tech Solutions is dedicated to improving the quality of life through cutting-edge Water Purification Systems, Milk ATMs, Water Vending Machines, Cooking Oil ATMs, and Pasteurizers.
            </p>
            <div className="text-xs text-sky-400 font-semibold tracking-wide italic">
              "Purify life one drop at a time"
            </div>
            {/* Social Media icons from screenshot */}
            <div className="pt-2">
              <span className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Follow Us</span>
              <div className="flex items-center gap-2.5">
                {/* Facebook */}
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Facebook"
                  className="w-9 h-9 rounded-full bg-[#1877F2]/20 text-[#1877F2] hover:bg-[#1877F2] hover:text-white flex items-center justify-center transition-colors border border-white/10"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                {/* Instagram */}
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 rounded-full bg-rose-500/20 text-rose-400 hover:bg-rose-600 hover:text-white flex items-center justify-center transition-colors border border-white/10"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                {/* X */}
                <a
                  href="https://x.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="X (formerly Twitter)"
                  className="w-9 h-9 rounded-full bg-slate-800 text-slate-200 hover:bg-black hover:text-white flex items-center justify-center transition-colors border border-white/10"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Column 2: Equipment Solutions */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-200 font-bold mb-4">
              Machinery & Systems
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li>
                <a
                  href="/products"
                  onClick={(e) => handleNav('products', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Water Purification Systems (RO)
                </a>
              </li>
              <li>
                <a
                  href="/products"
                  onClick={(e) => handleNav('products', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Milk ATM Vending Machines
                </a>
              </li>
              <li>
                <a
                  href="/products"
                  onClick={(e) => handleNav('products', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Water Vending & Filling Stations
                </a>
              </li>
              <li>
                <a
                  href="/products"
                  onClick={(e) => handleNav('products', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Cooking Oil & Salad ATMs
                </a>
              </li>
              <li>
                <a
                  href="/products"
                  onClick={(e) => handleNav('products', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  300L Batch Pasteurizers
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => handleNav('services', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Genuine Spare Parts & Pumps
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Quick Navigation & Tools */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-200 font-bold mb-4">
              Resources & Tools
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li>
                <a
                  href="/calculator"
                  onClick={(e) => handleNav('calculator', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Business ROI & Profit Calculator
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  onClick={(e) => handleNav('blog', e)}
                  className="hover:text-sky-400 transition-colors text-left block font-medium text-slate-300"
                >
                  Blog & Market Insights
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => handleNav('services', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Book a Technician On-Site
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  onClick={(e) => handleNav('about', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Mission, Vision & Story
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  onClick={(e) => handleNav('about', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Commitment to Sustainability
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  onClick={(e) => handleNav('contact', e)}
                  className="hover:text-sky-400 transition-colors text-left block"
                >
                  Location & Driving Directions
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact & Office Details (Exact from screenshot) */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-200 font-bold mb-4">
              Nairobi Office
            </h4>
            <div className="space-y-3.5 text-sm text-slate-400">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
                <span>
                  Juja Road 478/B Green Mall.<br />
                  Nairobi, Kenya.
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <a
                  href="tel:+254727072393"
                  className="hover:text-white transition-colors tabular-nums font-medium text-slate-200"
                >
                  +254 727 072 393
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-sky-400 shrink-0" />
                <a
                  href="mailto:info@saintstech.co.ke"
                  className="hover:text-white transition-colors text-slate-200"
                >
                  info@saintstech.co.ke
                </a>
              </div>
              <div className="flex items-start gap-2.5">
                <Clock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-200">Mon - Fri : 9am - 5pm</p>
                  <p className="text-xs text-slate-500">Weekend: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Legal Links */}
        <div className="mt-12 pt-6 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-sky-500" />
            <span>&copy; {currentYear} Saints Tech Solutions. All rights reserved.</span>
          </div>
          <nav aria-label="Legal" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {[
              { page: 'privacy-policy', label: 'Privacy Policy' },
              { page: 'terms', label: 'Terms of Use' },
              { page: 'disclaimer', label: 'Disclaimer' }
            ].map(({ page, label }) => (
              <a
                key={page}
                href={buildCleanPath(page)}
                onClick={(e) => handleNav(page, e)}
                className="hover:text-sky-400 transition-colors"
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};
